// Journey step definitions + state resolution logic

export const nb = (v) => v != null && String(v).trim() !== "";

// The group's WhatsApp side is gone (deleted by the automation or by hand in
// WhatsApp). The row and its history stay; nothing can be sent into it.
export const isWaGroupDeleted = (g) => nb(g?.["WA Deleted At"]);

// "2 Nov 2026" for the deletion notice; the raw value if it is not a date.
export function waDeletedDay(g) {
  const raw = g?.["WA Deleted At"];
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? String(raw ?? "") : d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

// groups.escalation_flag has been written as "Yes"/"No" (sheet era) and lowercase
// "true"/"false" (Supabase era) at different times, so a strict === "Yes" check
// silently misses real escalations. Treat both spellings as the same signal.
export const isEscalationFlagged = (v) => ["true", "yes"].includes(String(v ?? "").trim().toLowerCase());

// Values that mean a step is rejected (red) vs needs attention (amber)
const REJECT = ["reject", "rejected", "needs improvement"];
const CLARIFY = ["ask to clarify", "clarify", "callback"];
const SUCCESS_SUB = ["gatepass created"];

// What a paper stage (estimate, job card, invoices) may be uploaded as. The API
// enforces the same list (STAGE_UPLOAD_KINDS in server.py) — keep them in step.
export const DOCUMENT_ACCEPT = ".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.html,.htm";
// Steps whose paper may also be photographed (job card, pre-invoice, invoice).
export const PHOTO_OR_DOCUMENT_ACCEPT = `${DOCUMENT_ACCEPT},image/*`;

// What a stage whose template carries one file type may be uploaded as: that type.
export const FILE_TYPE_ACCEPT = { document: DOCUMENT_ACCEPT, image: "image/*", video: "video/*" };

// Whether a picked file matches an <input accept> string: ".ext" entries match
// the file's extension, "type/*" entries its MIME type. The picker's own filter
// is only a hint (the user can switch it to "All files"), so this re-checks.
export function fileMatchesAccept(file, accept) {
  if (!accept) return true;
  const name = String(file?.name || "").toLowerCase();
  const type = String(file?.type || "").toLowerCase();
  return accept
    .split(",")
    .map((a) => a.trim().toLowerCase())
    .filter(Boolean)
    .some((a) => {
      if (a.startsWith(".")) return name.endsWith(a);
      // Some browsers hand over no MIME type (a .heic, say); the API checks the
      // extension for those, so don't turn a good photo away here.
      if (!type) return true;
      return a.endsWith("/*") ? type.startsWith(a.slice(0, -1)) : type === a;
    });
}

export const STEP_DEFS = [
  { key: "created", label: "Group Created", done: (g) => g["Status"] === "Created" },
  {
    key: "welcome",
    label: "Welcome Message",
    done: (g) => g["Welcome Sent"] === "Sent",
  },
  { key: "invites", label: "Invitations Sent", done: (g) => nb(g["Invite Link"]) },
  {
    key: "initial",
    label: "Escalation Metrics Sent",
    // Sheet sometimes stores checkbox/boolean TRUE instead of dropdown "Sent"
    // Display label only — the underlying sheet/DB column is still "Initial Message Sent".
    done: (g) => {
      const v = String(g["Initial Message Sent"] ?? "").trim().toLowerCase();
      return v === "sent" || v === "true";
    },
  },
  {
    key: "media",
    label: "Inventory Details Sent",
    done: (g) => g["Media Sent"] === "Sent",
    upload: {
      stage: "media",
      linkCol: "Media Link",
      notesCol: "Media Notes",
      sentCol: "Media Sent",
      title: "Inventory Details",
      mediaTypeChoice: true,
      multiple: true,
    },
  },
  {
    key: "estimate",
    label: "Estimate Sent",
    done: (g) => g["Estimate Sheet Sent"] === "Sent",
    sub: (g) => g["Estimate Sheet Status"],
    statusCol: "Estimate Sheet Status",
    upload: {
      stage: "estimate",
      linkCol: "Estimate Sheet Link",
      sentCol: "Estimate Sheet Sent",
      title: "Estimate",
      accept: DOCUMENT_ACCEPT,
      status: { col: "Estimate Sheet Status", title: "Estimate status" },
    },
  },
  {
    key: "jobcard",
    label: "Job Card Created",
    done: (g) => g["Job Card Sent"] === "Sent",
    upload: {
      stage: "job_card",
      linkCol: "Job Card Link",
      sentCol: "Job Card Sent",
      title: "Job Card",
      accept: PHOTO_OR_DOCUMENT_ACCEPT,
      camera: true,
    },
  },
  {
    key: "progress",
    label: "Work in Progress",
    done: (g) => g["Work Progress Sent"] === "Sent",
    upload: {
      stage: "work_progress",
      linkCol: "Work Progress Link",
      notesCol: "Work Progress Notes",
      sentCol: "Work Progress Sent",
      title: "Work Progress",
      mediaTypeChoice: true,
      multiple: true,
    },
  },
  {
    key: "parts_approval",
    label: "Parts Approval",
    done: (g) => g["Parts Approval Sent"] === "Sent" || ["Approve", "Approved"].includes(String(g["Parts Approval Status"] || "").trim()),
    sub: (g) => g["Parts Approval Status"],
    statusCol: "Parts Approval Status",
    partsApproval: true,
  },
  {
    key: "parts",
    label: "Parts Recommendation",
    show: (g) => g["Parts Reco Required"] === "Yes",
    done: (g) => g["Parts Reco Sent"] === "Sent",
    sub: (g) => g["Parts Reco Status"],
    statusCol: "Parts Reco Status",
  },
  {
    key: "pre_invoice",
    label: "Pre Invoice Sent",
    done: (g) => g["Pre Invoice Sent"] === "Sent",
    sub: (g) => g["Pre Invoice Approval"],
    statusCol: "Pre Invoice Approval",
    upload: {
      stage: "pre_invoice",
      linkCol: "Pre Invoice Link",
      sentCol: "Pre Invoice Sent",
      title: "Pre Invoice",
      accept: PHOTO_OR_DOCUMENT_ACCEPT,
      camera: true,
      status: { col: "Pre Invoice Approval", title: "Pre Invoice approval" },
    },
  },
  {
    key: "invoice",
    label: "Invoice",
    done: (g) => g["Invoice Sent"] === "Sent",
    sub: (g) => g["Invoice Approval"],
    statusCol: "Invoice Approval",
    upload: {
      stage: "invoice",
      linkCol: "Invoice Link",
      sentCol: "Invoice Sent",
      title: "Invoice",
      accept: PHOTO_OR_DOCUMENT_ACCEPT,
      camera: true,
      status: { col: "Invoice Approval", title: "Invoice approval" },
    },
  },
  {
    key: "pre_delivery_feedback",
    label: "Pre Delivery Feedback",
    done: (g) => g["Pre Delivery Feedback Sent"] === "Sent",
    sub: (g) => g["Pre Delivery Feedback Status"],
    statusCol: "Pre Delivery Feedback Status",
  },
  {
    key: "vehicle_delivered",
    label: "Vehicle Delivered",
    done: (g) => nb(g["Delivery Notified"]),
    sub: (g) => {
      const opt = g["Delivery Option"];
      const status = g["Delivery Status"];
      const booked = g["Pick & Drop"];
      // Changed at the delivery step from the option chosen at booking.
      const edited = nb(opt) && nb(booked) && opt !== booked ? " (edited)" : "";
      if (nb(status) && nb(opt)) return `${status} · ${opt}${edited}`;
      return status || (opt ? `${opt}${edited}` : "");
    },
    upload: {
      stage: "delivery_media",
      linkCol: "Delivery Media Link",
      sentCol: "Delivery Media Sent",
      title: "Delivery Media",
      mediaTypeChoice: true,
      multiple: true,
    },
    delivery: true,
  },
  {
    key: "next_service_due",
    label: "Next Service Due",
    done: (g) => g["Next Service Due Sent"] === "Sent",
  },
  {
    key: "post_delivery_feedback",
    label: "Post Delivery Feedback",
    done: (g) => g["Post Delivery Feedback Sent"] === "Sent",
  },
  {
    key: "feedback",
    label: "Feedback",
    done: (g) => nb(g["Feedback Notified"]),
    feedback: true,
  },
];

// Applies a tenant's terminology overlay to STEP_DEFS.
//
// STEP_DEFS stays the canonical definition — the overlay only renames, reorders
// and hides. Each overlay row is matched by `stage_key` to a STEP_DEFS `key`,
// so a brand can call "Estimate Sent" whatever it likes without any of the
// underlying done()/upload column bindings moving.
//
// Order comes from each row's `sort_order` (the server writes it as the array
// index of the superadmin's list). A stage the overlay does not mention keeps
// its default position, so a partial overlay renames in place rather than
// yanking that stage to the front.
// Shared ordering step behind applyStageOverlay and orderedStageKeys: pairs
// each def with its overlay row (if any) and sorts by sort_order, falling
// back to the default index for ties or an unmentioned stage.
function sortByOverlay(defs, overlay) {
  const byKey = new Map((overlay || []).map((o) => [o.stage_key, o]));
  return defs
    .map((d, i) => {
      const o = byKey.get(d.key);
      const pos = typeof o?.sort_order === "number" ? o.sort_order : i;
      return { def: d, overlay: o, pos, fallback: i };
    })
    .sort((a, b) => a.pos - b.pos || a.fallback - b.fallback);
}

// Stages the webhook can mark done from a chat keyword: every stage a staff
// member can do by hand in the group, so not the two the system does itself
// (group created, invitations). Keep in sync with _CAPTURE_SENT_COLUMNS in
// apps/webhook/handlers/group_events.py.
export const CAPTURE_WIRED_STAGES = new Set(
  STEP_DEFS.map((s) => s.key).filter((k) => k !== "created" && k !== "invites"),
);

// Whether a stage's template is forced off: the stage is hidden, or
// Capture from WhatsApp is on for it — mirrors the API's force-disable on
// save. `overlayRow` is that stage_key's row from GET tenant journey-stages,
// or undefined if the tenant never touched it (nothing locked).
export function stageTemplateLock(stageKey, overlayRow) {
  if (overlayRow?.is_visible === false) {
    return { locked: true, reason: "Stage hidden" };
  }
  if (overlayRow?.capture_from_whatsapp) {
    return { locked: true, reason: "Captured from WhatsApp" };
  }
  return { locked: false, reason: "" };
}

export function applyStageOverlay(defs, overlay) {
  if (!overlay || overlay.length === 0) return defs;

  return sortByOverlay(defs, overlay)
    .filter((e) => (e.overlay ? e.overlay.is_visible !== false : true))
    .map((e) => {
      const capture = (CAPTURE_WIRED_STAGES.has(e.def.key) || !!e.def.custom) && !!e.overlay?.capture_from_whatsapp;
      // A stage's saved settings (the starter journey's, for a built-in stage on the stage engine).
      const blocks = e.def.blocks || (e.overlay?.blocks?.trigger ? e.overlay.blocks : undefined);
      if (e.overlay?.label) {
        return { ...e.def, label: e.overlay.label, capture_from_whatsapp: capture, blocks };
      }
      // Only spread when we must attach the flag — keep referential identity otherwise.
      if (capture || e.overlay?.blocks?.trigger) return { ...e.def, capture_from_whatsapp: capture, blocks };
      return e.def;
    });
}

// Same sort_order ordering as applyStageOverlay, but keeps every stage —
// visible or hidden. Used where hiding a stage must not make its row (e.g. a
// disabled template) disappear, just fall in the right place.
export function orderedStageKeys(defs, overlay) {
  if (!overlay || overlay.length === 0) return defs.map((d) => d.key);
  return sortByOverlay(defs, overlay).map((e) => e.def.key);
}

// Returns array of visible steps with resolved state.
// `stageOverlay` is optional — omitted, this behaves exactly as it always has.
// `featureFlags.pre_delivery_feedback_chain` must be true for the Pre Delivery
// stage to appear; a brand without the chain keeps the old end-of-journey
// Feedback step instead.
// A stage the brand added in Super Admin > Service journey. Where a group is in it comes from
// the group's "Custom Stages" ({stage_key: {status, decision}}), which GET /groups fills.
export function customStageDef(row) {
  const blocks = row.blocks || {};
  const state = (g) => (g?.["Custom Stages"] || {})[row.stage_key] || {};
  return {
    key: row.stage_key,
    label: row.label,
    custom: true,
    blocks,
    done: (g) => state(g).status === "done",
    sub: (g) => state(g).decision || (state(g).status === "sent" ? "Sent" : ""),
    customState: (g) => state(g).status || "",
  };
}

// Built-in definitions plus the brand's own stages, so every list orders them the same way.
export function withCustomStages(defs, overlay) {
  const custom = (overlay || []).filter((o) => o.is_custom).map(customStageDef);
  return custom.length ? [...defs, ...custom] : defs;
}

export function resolveJourney(g, stageOverlay, featureFlags) {
  let defs = applyStageOverlay(withCustomStages(STEP_DEFS, stageOverlay), stageOverlay);
  if (featureFlags?.pre_delivery_feedback_chain !== true) {
    defs = defs.filter((s) => s.key !== "pre_delivery_feedback");
  } else {
    // The follow-up chain (Pre Delivery -> Next Service Due -> Post Delivery)
    // replaces the old end-of-journey Feedback step, for every brand on it
    // (Hyundai-style brands too): Pre and Post Delivery Feedback already cover it.
    defs = defs.filter((s) => s.key !== "feedback");
  }
  const steps = defs.filter((s) => (s.show ? s.show(g) : true)).map((s) => {
    const done = s.done(g);
    const subVal = s.sub ? s.sub(g) : "";
    let attention = null; // 'reject' | 'clarify' | 'success'
    if (s.statusCol) {
      const v = String(g[s.statusCol] || "").trim().toLowerCase();
      if (REJECT.includes(v)) attention = "reject";
      else if (CLARIFY.includes(v)) attention = "clarify";
      else if (SUCCESS_SUB.includes(v)) attention = "success";
    }
    return { ...s, done, sub: subVal, attention };
  });

  let lastCompleted = -1;
  steps.forEach((s, i) => {
    if (s.done) lastCompleted = i;
  });

  return steps.map((s, i) => {
    let state;
    // "completed"/"current" require the step's OWN done flag — being
    // positioned before the furthest genuine progress is not the same as
    // having actually happened. A manually-triggered stage (Welcome, Next
    // Service Due) that nobody has sent yet must not show a checkmark just
    // because a later, automatic stage went ahead of it.
    if (s.done && i < lastCompleted) state = "completed";
    else if (s.done && i === lastCompleted) state = "current";
    else if (!s.done && i === lastCompleted + 1) state = "next";
    else state = "future";
    return { ...s, state };
  });
}

export const SERVICE_TYPES = ["FR1", "FR2", "FR3", "PMS", "Running Repair", "Service Camp", "Body Shop", "Washing"];

// Chosen at booking. The stored words match Delivery Option's, the label is what staff read.
export const PICKUP_DROP_OPTIONS = [
  ["Pickup/Drop", "Pick & drop"],
  ["Self Visit", "Self visit"],
];

export const STATUS_META = {
  Create: { label: "Create", cls: "bg-slate-100 text-slate-700 border-slate-200" },
  "Pending Sync": { label: "Pending Sync", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  Created: { label: "Created", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  Error: { label: "Error", cls: "bg-red-50 text-red-700 border-red-200" },
};